/**
 * Circle of Fifths Wheel v1 (Phase 3 Tier 3) — pure-function core.
 *
 * Two sub-modes: Wheel Explore (free-explore, key-info + closely-related
 * cluster) and Key Relationship Quiz (10-Q session, two question kinds:
 * find-relative + find-closely-related). All key data — pool, signature
 * deltas, closely-related cluster math — is Bowie-locked verbatim from
 * the scope memo.
 *
 * Spec: docs/superpowers/specs/2026-05-23-cof-wheel-v1-design.md §4
 * Bowie scope memo: .claude/agent-memory/bowie/project_cof_wheel_v1_scope.md
 */

export type CanonicalMajor =
  | 'C'
  | 'G'
  | 'D'
  | 'A'
  | 'E'
  | 'B'
  | 'F#'
  | 'C#'
  | 'G#'
  | 'D#'
  | 'A#'
  | 'F'

export interface KeySlot {
  canonical: CanonicalMajor
  position: number
  majorLabels: readonly string[]
  relativeMinor: string
  signatureDelta: number
}

export const KEY_POOL: readonly KeySlot[] = [
  { canonical: 'C', position: 0, majorLabels: ['C'], relativeMinor: 'A', signatureDelta: 0 },
  { canonical: 'G', position: 1, majorLabels: ['G'], relativeMinor: 'E', signatureDelta: 1 },
  { canonical: 'D', position: 2, majorLabels: ['D'], relativeMinor: 'B', signatureDelta: 2 },
  { canonical: 'A', position: 3, majorLabels: ['A'], relativeMinor: 'F#', signatureDelta: 3 },
  { canonical: 'E', position: 4, majorLabels: ['E'], relativeMinor: 'C#', signatureDelta: 4 },
  { canonical: 'B', position: 5, majorLabels: ['B', 'Cb'], relativeMinor: 'G#', signatureDelta: 5 },
  { canonical: 'F#', position: 6, majorLabels: ['F#', 'Gb'], relativeMinor: 'D#', signatureDelta: 6 },
  { canonical: 'C#', position: 7, majorLabels: ['C#', 'Db'], relativeMinor: 'A#', signatureDelta: 7 },
  { canonical: 'G#', position: 8, majorLabels: ['Ab'], relativeMinor: 'F', signatureDelta: -4 },
  { canonical: 'D#', position: 9, majorLabels: ['Eb'], relativeMinor: 'C', signatureDelta: -3 },
  { canonical: 'A#', position: 10, majorLabels: ['Bb'], relativeMinor: 'G', signatureDelta: -2 },
  { canonical: 'F', position: 11, majorLabels: ['F'], relativeMinor: 'D', signatureDelta: -1 },
]

/** Enharmonic minor-key spelling pairs. */
const MINOR_ENHARMONICS: Record<string, string> = {
  'D#': 'Eb',
  Eb: 'D#',
  'G#': 'Ab',
  Ab: 'G#',
  'A#': 'Bb',
  Bb: 'A#',
}

/** Enharmonic major-key spelling pairs. */
const MAJOR_ENHARMONICS: Record<string, string> = {
  'F#': 'Gb',
  Gb: 'F#',
  'C#': 'Db',
  Db: 'C#',
  B: 'Cb',
  Cb: 'B',
  Ab: 'G#',
  'G#': 'Ab',
  Eb: 'D#',
  'D#': 'Eb',
  Bb: 'A#',
  'A#': 'Bb',
}

const expandMajorSpellings = (name: string): readonly string[] => {
  const alt = MAJOR_ENHARMONICS[name]
  return alt ? [name, alt] : [name]
}

const expandMinorSpellings = (name: string): readonly string[] => {
  const alt = MINOR_ENHARMONICS[name]
  return alt ? [`${name}m`, `${alt}m`] : [`${name}m`]
}

const findSlotByMajorName = (name: string): KeySlot | undefined =>
  KEY_POOL.find(slot => slot.majorLabels.includes(name) || slot.canonical === name)

export const getCloselyRelatedKeys = (majorKey: string): readonly string[] => {
  const slot = findSlotByMajorName(majorKey)
  if (!slot) {
    return []
  }
  const p = slot.position
  const ivMaj = KEY_POOL[(p + 11) % 12].canonical
  const vMaj = KEY_POOL[(p + 1) % 12].canonical
  const viMin = `${slot.relativeMinor}m`
  const iiMin = `${KEY_POOL[(p + 11) % 12].relativeMinor}m`
  const iiiMin = `${KEY_POOL[(p + 1) % 12].relativeMinor}m`
  return [ivMaj, vMaj, viMin, iiMin, iiiMin]
}

export const getRelativeKey = (
  key: string,
  ring: 'major' | 'minor'
): { name: string; ring: 'major' | 'minor' } => {
  if (ring === 'major') {
    const slot = findSlotByMajorName(key)
    if (!slot) {
      return { name: key, ring: 'minor' }
    }
    return { name: slot.relativeMinor, ring: 'minor' }
  }
  // minor ring → find the slot whose relativeMinor matches (or matches an enharmonic spelling).
  const alt = MINOR_ENHARMONICS[key]
  const slot = KEY_POOL.find(s => s.relativeMinor === key || (alt !== undefined && s.relativeMinor === alt))
  if (!slot) {
    return { name: key, ring: 'major' }
  }
  return { name: slot.canonical, ring: 'major' }
}

// ─── Quiz machinery ───

export type CofQuizQuestionKind = 'find-relative' | 'find-closely-related'

export interface CofQuizQuestion {
  kind: CofQuizQuestionKind
  referenceKey: { name: string; ring: 'major' | 'minor' }
  acceptedAnswers: readonly string[]
  promptLabel: string
}

const expandClusterSpellings = (cluster: readonly string[]): readonly string[] => {
  const out = new Set<string>()
  cluster.forEach(name => {
    if (name.endsWith('m')) {
      expandMinorSpellings(name.replace(/m$/, '')).forEach(n => out.add(n))
    } else {
      expandMajorSpellings(name).forEach(n => out.add(n))
    }
  })
  return [...out]
}

export const generateCofQuizQuestion = (rng: () => number = Math.random): CofQuizQuestion => {
  const slot = KEY_POOL[Math.min(KEY_POOL.length - 1, Math.floor(rng() * KEY_POOL.length))]
  const ring: 'major' | 'minor' = rng() < 0.5 ? 'major' : 'minor'
  const kind: CofQuizQuestionKind = rng() < 0.5 ? 'find-relative' : 'find-closely-related'

  if (kind === 'find-relative') {
    if (ring === 'major') {
      return {
        kind,
        referenceKey: { name: slot.canonical, ring: 'major' },
        acceptedAnswers: expandMinorSpellings(slot.relativeMinor),
        promptLabel: `${slot.canonical} major`,
      }
    }
    return {
      kind,
      referenceKey: { name: slot.relativeMinor, ring: 'minor' },
      acceptedAnswers: expandMajorSpellings(slot.canonical),
      promptLabel: `${slot.relativeMinor} minor`,
    }
  }

  // find-closely-related — always framed as the major reference (Bowie §"Minor-ring reference handling").
  const cluster = getCloselyRelatedKeys(slot.canonical)
  return {
    kind,
    referenceKey: { name: slot.canonical, ring: 'major' },
    acceptedAnswers: expandClusterSpellings(cluster),
    promptLabel: `${slot.canonical} major`,
  }
}

export interface CofQuizVerdict {
  correct: boolean
  picked: { name: string; ring: 'major' | 'minor' }
  question: CofQuizQuestion
}

export const validateCofQuizAnswer = (
  q: CofQuizQuestion,
  picked: { name: string; ring: 'major' | 'minor' }
): CofQuizVerdict => {
  const pickStr = picked.ring === 'minor' ? `${picked.name}m` : picked.name
  return {
    correct: q.acceptedAnswers.includes(pickStr),
    picked,
    question: q,
  }
}

export interface CofQuizResult {
  question: CofQuizQuestion
  verdict: CofQuizVerdict
}

export interface CofSessionStats {
  total: number
  correct: number
  byKind: Record<CofQuizQuestionKind, { total: number; correct: number }>
}

export const computeCofSessionStats = (results: readonly CofQuizResult[]): CofSessionStats => {
  const stats: CofSessionStats = {
    total: results.length,
    correct: results.filter(r => r.verdict.correct).length,
    byKind: {
      'find-relative': { total: 0, correct: 0 },
      'find-closely-related': { total: 0, correct: 0 },
    },
  }
  results.forEach(r => {
    const bucket = stats.byKind[r.question.kind]
    bucket.total += 1
    if (r.verdict.correct) {
      bucket.correct += 1
    }
  })
  return stats
}
